/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Box, Stack, Typography } from '@mui/material';
import {
  CATEGORY_DEFAULT_OPTION,
  PAGINATION_DATA_LIMIT,
  SortingOptions,
} from '../../../../../util/consts';
import { ImageContainer } from '../../../../products/components/ProductCard/ProductCardParent';
import PlaceholderImage from '../../../../../assets/images/image-placeholder.png';
import React from 'react';
import { Button } from '../../../../../components/Button';
import { multiSearchQuery } from '../../../../typesense/api';
import { TypesenseProduct } from '../../../../typesense/types';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../../../../../services/api-client';
import { ActivePrice, ActivePrices } from '../../../../products/storage';
import { useSelector } from 'react-redux';
import { selectCurrentFarmerAccount } from '../../../../azure-b2c/storage';
import { FilterState } from './ProductListFilter';
import Pagination, {
  PaginationCounter,
  PaginationDetails,
} from '../../../../../components/Pagination';
import TestIds from '../../../../../testIds';
import { Loader, LoadingBox } from '../../../../../components/Loader';
import { generateImageUrl } from '../../../../../util/helpers';
import useDebounce from '../../../../../util/useDebounce';
import { CartItem } from '../../../../cart/storage/cart.types';
import { fetchProductDetail } from '../../../../products/utils/fetchProductDetail';
import { useBranch } from '../../../../../util/useBranch';
import { ItemLabel } from '../../../../../components/styled';

export const sortOptions = [
  SortingOptions.BestMatch,
  SortingOptions.PriceAscending,
  SortingOptions.PriceDescending,
];

interface ProductListViewProps {
  filterState: FilterState;
  setFilterState: React.Dispatch<Partial<FilterState>>;
  onAddProduct: (product: CartItem) => void;
  onEditProduct: (product: EditProduct) => void;
}

export interface EditProduct extends TypesenseProduct {
  imageUrl: string;
  activePrice: ActivePrice | undefined;
}
const ProductListView: React.FC<ProductListViewProps> = ({
  filterState,
  setFilterState,
  onAddProduct,
  onEditProduct,
}: ProductListViewProps) => {
  const account = useSelector(selectCurrentFarmerAccount);
  const currentAccount = useSelector(selectCurrentFarmerAccount);
  const searchValue = useDebounce(filterState.searchText, 1000);
  const branchId = useBranch();

  const categoryFilterValue = React.useMemo(() => {
    if (filterState.category && filterState.category !== CATEGORY_DEFAULT_OPTION) {
      return filterState.category.split('>').reduce((carry, category, index) => {
        if (index > 0) {
          carry += ` && l${index + 1}_category:=${category}`;
        } else {
          carry += `l${index + 1}_category:=${category}`;
        }
        return carry;
      }, '');
    }
  }, [filterState.category]);
  const sortingValue = React.useMemo(() => {
    switch (filterState.sortBy) {
      case sortOptions[0]:
        return '_text_match:desc';
      case sortOptions[1]:
        return 'price:asc';
      case sortOptions[2]:
        return 'price:desc';
      default:
        return '_text_match:desc';
    }
  }, [filterState.sortBy]);

  // if the value of keys change, it will refetch the api
  const queryFilterKey = [filterState.currentPage, searchValue, categoryFilterValue, sortingValue];

  const allProductQuery = useQuery({
    queryKey: ['multiSearch', ...queryFilterKey],
    queryFn: async () => {
      const searchString = searchValue === '' || !searchValue ? '*' : searchValue;
      const { searchResults } = await multiSearchQuery<TypesenseProduct>({
        searches: [
          {
            preset: 'all_products_search',
            per_page: PAGINATION_DATA_LIMIT,
            page: filterState.currentPage,
            q: searchString,
            sort_by: sortingValue,
            filter_by: categoryFilterValue,
          },
        ],
      });
      return searchResults;
    },
  });

  const getProductsActivePriceQuery = useQuery<ActivePrice[]>({
    queryKey: ['get-active-price', ...queryFilterKey],
    queryFn: async () => {
      if (!currentAccount) {
        throw new Error('Account Missing');
      }

      const response = await apiClient.post<
        ActivePrices,
        { accountNumber: string; productIds: number[] }
      >(`/products/get-active-prices`, {
        accountNumber: currentAccount?.accountNumber,
        productIds: products.map((product) => Number(product.id)),
      });
      return response.data.prices;
    },
    enabled: allProductQuery.isSuccess,
  });

  const isLoading = getProductsActivePriceQuery.isLoading || allProductQuery.isLoading;

  const products = React.useMemo(() => {
    if (allProductQuery.data) {
      return allProductQuery.data.hits?.map((hit) => hit.document) ?? [];
    }
    return [];
  }, [allProductQuery.data]);

  const { total, count } = React.useMemo(() => {
    return {
      total: allProductQuery.data?.found ?? 0,
      count: allProductQuery.data?.hits?.length ?? 0,
    };
  }, [allProductQuery.data]);

  const getProductPrice = React.useCallback(
    (productId: number) => {
      if (Array.isArray(getProductsActivePriceQuery.data)) {
        return getProductsActivePriceQuery.data.find(
          (productPrice) => productPrice.productId === productId,
        );
      }
    },
    [getProductsActivePriceQuery.data],
  );

  const isFilterApplied = React.useMemo(() => {
    return Boolean(filterState.category !== CATEGORY_DEFAULT_OPTION || filterState.searchText);
  }, [filterState.category, filterState.searchText]);

  function handleClearFilters() {
    setFilterState({
      category: CATEGORY_DEFAULT_OPTION,
      searchText: undefined,
    });
  }

  async function handleAddProduct(productId: string) {
    try {
      const productDetail = await fetchProductDetail(productId, account?.accountNumber, branchId);
      const activePrice = getProductPrice(Number(productId));
      onAddProduct({
        ...productDetail,
        ...activePrice,
        quantity: 1,
      });
    } catch (error) {
      console.log('failed to add product');
    }
  }

  if (isLoading) {
    return (
      <LoadingBox>
        <Loader title='Loading your products' type='inline-flex' sx={{ marginTop: '12px' }} />
      </LoadingBox>
    );
  }

  return (
    <Stack direction='column' width='100%' gap={2} overflow='hidden'>
      <Stack direction='column' gap={3}>
        <PaginationDetails
          onClear={handleClearFilters}
          showClear={isFilterApplied}
          paginationCounter={
            <PaginationCounter
              data-testid={TestIds.feat.quotes.details.addProductModal.listPaginationCounter}
              count={count}
              total={total}
              currentPage={filterState.currentPage}
              itemsPerPage={PAGINATION_DATA_LIMIT}
              label='products'
            />
          }
          sorting={{
            value: filterState.sortBy,
            onChange: (option) => setFilterState({ sortBy: option }),
            options: sortOptions,
          }}
        />
        {products.map((product) => {
          const activePrice = getProductPrice(Number(product.id));
          const productImageUrl = generateImageUrl(product.item_id);
          return (
            <ProductCard
              key={product.id}
              name={product.name}
              sku={product.item_id}
              imageUrl={productImageUrl}
              activePrice={activePrice?.basePriceExcGst !== 0 ? activePrice : undefined}
              hasVariants={product.has_variants}
              onClick={() => {
                if (product.has_variants) {
                  onEditProduct({
                    ...product,
                    imageUrl: generateImageUrl(product.item_id),
                    activePrice,
                  });
                } else {
                  handleAddProduct(String(product.id));
                }
              }}
            />
          );
        })}
        <Pagination
          page={filterState.currentPage}
          count={Math.ceil(total / PAGINATION_DATA_LIMIT)}
          onClickPageNumber={(value) => {
            setFilterState({ currentPage: value });
          }}
        />
      </Stack>
    </Stack>
  );
};

interface ProductCardProps extends Partial<TypesenseProduct> {
  imageUrl: string;
  activePrice: ActivePrice | undefined;
  sku: string;
  hasVariants: boolean;
  onClick: () => void;
}

const ProductCard: React.FC<ProductCardProps> = ({
  name,
  activePrice,
  sku,
  imageUrl,
  onClick,
  hasVariants,
}) => {
  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      gap={2}
      padding={2}
      border={1}
      borderColor='#eaefe6'
    >
      <Box>
        <ImageContainer
          src={imageUrl}
          placeholderImageUrl={PlaceholderImage}
          data-testid={TestIds.feat.product.card.image}
          alt=''
        />
      </Box>
      <Stack direction='column' gap={1} flex={1}>
        <Typography variant='h4'>{name}</Typography>
        {activePrice ? (
          <Stack direction='row' gap={2}>
            <Stack direction='row' gap={1}>
              <Typography variant='body2' color='grey700'>
                Your price:
              </Typography>
              <Typography variant='body2' color='#ac622a' fontWeight='bold'>
                ${activePrice.discountedPriceExcGst.toFixed(2)}
              </Typography>
            </Stack>
            <Stack direction='row' gap={1}>
              <Typography variant='body2' color='grey700'>
                RRP:
              </Typography>
              <Typography
                variant='body2'
                color='grey700'
                style={{ textDecoration: 'line-through' }}
              >
                ${activePrice.basePriceExcGst.toFixed(2)}
              </Typography>
            </Stack>
          </Stack>
        ) : (
          <ItemLabel variant='body2' data-testid={TestIds.feat.product.card.priceNotAvailable}>
            Price unavailable
          </ItemLabel>
        )}
        <Typography variant='body2' color='grey700'>
          SKU: {sku}
        </Typography>
      </Stack>
      {activePrice ? (
        <Stack direction='column' justifyContent='flex-end'>
          <Button variant='contained' onClick={onClick}>
            {hasVariants ? 'Show Options' : 'Add to quote'}
          </Button>
        </Stack>
      ) : null}
    </Stack>
  );
};

export default ProductListView;
